import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Activity, MonitorSmartphone, Users, ClipboardList, Radio, LogOut, Gauge, ShieldCheck, ExternalLink } from "lucide-react";
import BrandMark from "./BrandMark";
import { api, getManager, clearManagerSession, peopleEngineLink, PEOPLE_ENGINE_URL } from "../lib/api";

const NAV = [
  { to: "/control/dashboard", label: "Live Feed", icon: Activity, testid: "nav-dashboard" },
  { to: "/control/ops", label: "Rollout Ops", icon: Gauge, testid: "nav-ops" },
  { to: "/control/access", label: "Access", icon: ShieldCheck, testid: "nav-access" },
  { to: "/control/devices", label: "Devices", icon: MonitorSmartphone, testid: "nav-devices" },
  { to: "/control/employees", label: "Employees", icon: Users, testid: "nav-employees" },
  { to: "/control/signals", label: "Signals", icon: Radio, testid: "nav-signals" },
  { to: "/control/audit", label: "Audit Log", icon: ClipboardList, testid: "nav-audit" },
];

export default function ControlLayout({ children, title, subtitle, actions }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [stats, setStats] = useState(null);
  const mgr = getManager();

  useEffect(() => {
    let alive = true;
    const fetchStats = () => api.get("/stats/overview")
      .then((r) => alive && setStats(r.data))
      .catch(() => {});
    fetchStats();
    const i = setInterval(fetchStats, 8000);
    return () => { alive = false; clearInterval(i); };
  }, [loc.pathname]);

  const logout = () => {
    clearManagerSession();
    nav("/control/login");
  };

  return (
    <div className="control-root min-h-screen bg-white text-neutral-900">
      {/* Top strip */}
      <header className="border-b border-neutral-200 bg-white">
        <div className="flex items-center h-14 px-5 gap-6">
          <button onClick={() => nav("/")} className="flex items-center gap-3" data-testid="brand-home">
            <BrandMark size={26} />
            <span className="mono-label">Control Room</span>
          </button>
          <div className="h-5 w-px bg-neutral-200" />
          <div className="mono-label">BENO Hospitality · BENO Assagao</div>
          <div className="ml-auto flex items-center gap-4">
            <a
              href={peopleEngineLink("/login")}
              target={PEOPLE_ENGINE_URL ? "_blank" : undefined}
              rel={PEOPLE_ENGINE_URL ? "noopener noreferrer" : undefined}
              data-testid="control-open-people-engine"
              className="inline-flex items-center gap-1 px-2 py-1 border border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100 font-mono text-[10px] uppercase tracking-wider"
              title="Open the People's Engine to take action — fresh login required"
            >
              <ExternalLink size={11} /> People&apos;s Engine
            </a>
            <StatChip label="Scans today" value={stats?.scans_today ?? "—"} testid="stat-scans-today" />
            <StatChip label="Open shifts" value={stats?.open_shifts ?? "—"} testid="stat-open-shifts" />
            <StatChip label="Reviews" value={stats?.pending_reviews ?? "—"} warn={stats?.pending_reviews > 0} testid="stat-reviews" />
            <StatChip label="Signals" value={stats?.signals_open ?? "—"} warn={stats?.signals_open > 0} testid="stat-signals" />
            <div className="h-6 w-px bg-neutral-200" />
            <div className="text-right">
              <div className="mono-label">{mgr?.role || "manager"}</div>
              <div className="text-xs font-medium">{mgr?.name}</div>
            </div>
            <button
              data-testid="btn-logout"
              onClick={logout}
              className="h-9 px-3 border border-neutral-300 hover:bg-neutral-900 hover:text-white transition-colors text-xs font-mono uppercase tracking-wider inline-flex items-center gap-2"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-12 min-h-[calc(100vh-56px)]">
        {/* Sidebar */}
        <aside className="col-span-12 lg:col-span-2 border-r border-neutral-200 bg-[#FAFAFA] py-4">
          <nav className="flex flex-col">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-3 border-l-2 transition-colors text-sm ${
                      isActive
                        ? "border-l-black bg-white font-semibold"
                        : "border-l-transparent text-neutral-600 hover:bg-white hover:text-black"
                    }`
                  }
                >
                  <Icon size={16} strokeWidth={1.75} />
                  {n.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="mt-6 px-5">
            <div className="mono-label mb-2">Read-only intelligence</div>
            <p className="text-[11px] leading-relaxed text-neutral-500 font-mono">
              Control Room observes &amp; governs. Workforce decisions live in
              the People&apos;s Engine.
            </p>
          </div>
        </aside>

        {/* Main */}
        <main className="col-span-12 lg:col-span-10">
          <div className="px-6 py-5 border-b border-neutral-200 flex items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-black tracking-tighter" data-testid="page-title">{title}</h1>
              {subtitle && <div className="mono-label mt-1">{subtitle}</div>}
            </div>
            <div>{actions}</div>
          </div>
          <div className="p-6">{children}</div>
        </main>
      </div>
    </div>
  );
}

function StatChip({ label, value, warn, testid }) {
  return (
    <div data-testid={testid} className={`px-3 py-1 border ${warn ? "border-[#EAB308] bg-[#FEF9C3]" : "border-neutral-200 bg-white"}`}>
      <div className="mono-label" style={{ fontSize: 9 }}>{label}</div>
      <div className="font-mono font-semibold text-sm leading-none">{value}</div>
    </div>
  );
}
